import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import { Webhook as WebhookIcon, Plus, Trash2, Copy, ShieldCheck, Activity, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog';

const EVENT_OPTIONS = [
  { value: 'transaction.created', label: 'transaction.created' },
  { value: 'transaction.updated', label: 'transaction.updated' },
  { value: 'transaction.deleted', label: 'transaction.deleted' },
  { value: 'budget.warning', label: 'budget.warning' },
  { value: 'budget.over', label: 'budget.over' },
  { value: 'recurring.materialized', label: 'recurring.materialized' },
];

const STATUS_CLASSES = {
  success: 'bg-emerald-50 text-emerald-700',
  pending: 'bg-sky-50 text-sky-700',
  failed: 'bg-amber-50 text-amber-700',
  dead: 'bg-red-50 text-red-700',
};

const Webhooks = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [hooks, setHooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState(['transaction.created']);
  const [createdHook, setCreatedHook] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [deliveries, setDeliveries] = useState({}); // { webhookId: [...] }
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);

  useEffect(() => {
    if (isAdmin) fetchHooks();
    else setLoading(false);
  }, [isAdmin]);

  const fetchHooks = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/webhooks`);
      setHooks(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  };

  const toggleEvent = (ev) => {
    setEvents((prev) => (prev.includes(ev) ? prev.filter((x) => x !== ev) : [...prev, ev]));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (events.length === 0) {
      toast.error('Pick at least one event');
      return;
    }
    try {
      const r = await axios.post(`${API}/webhooks`, { url, events });
      setCreatedHook(r.data);
      setUrl('');
      setEvents(['transaction.created']);
      fetchHooks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create webhook');
    }
  };

  const handleDelete = async (id, label) => {
    if (!window.confirm(`Delete webhook for "${label}"? Deliveries will stop immediately.`)) return;
    try {
      await axios.delete(`${API}/webhooks/${id}`);
      toast.success('Webhook deleted');
      fetchHooks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied'));
  };

  const fetchDeliveries = async (webhookId) => {
    setLoadingDeliveries(true);
    try {
      const r = await axios.get(`${API}/webhooks/${webhookId}/deliveries`, { params: { limit: 25 } });
      setDeliveries((prev) => ({ ...prev, [webhookId]: r.data || [] }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load deliveries');
    } finally {
      setLoadingDeliveries(false);
    }
  };

  const toggleExpand = (webhookId) => {
    if (expandedId === webhookId) {
      setExpandedId(null);
    } else {
      setExpandedId(webhookId);
      fetchDeliveries(webhookId);
    }
  };

  const handleRetryDelivery = async (deliveryId, webhookId) => {
    try {
      await axios.post(`${API}/webhooks/deliveries/${deliveryId}/retry`);
      toast.success('Delivery retried');
      fetchDeliveries(webhookId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to retry');
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-8 md:p-12" data-testid="webhooks-page">
        <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight">Webhooks</h1>
        <p className="mt-3 text-slate-600 font-ibm">Admin access is required to manage webhooks.</p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-12" data-testid="webhooks-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <WebhookIcon size={32} className="text-slate-900" />
            <div>
              <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight" data-testid="webhooks-title">
                Webhooks
              </h1>
              <p className="text-slate-600 mt-1 font-ibm">
                Receive HTTPS callbacks on events. Each payload is signed via <code className="text-xs">X-FinanceOS-Signature</code> (HMAC-SHA256).
              </p>
            </div>
          </div>
          <Dialog
            open={dialogOpen}
            onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) setCreatedHook(null);
            }}
          >
            <DialogTrigger asChild>
              <Button
                data-testid="create-webhook-button"
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm hover:scale-[0.98] transition-transform"
              >
                <Plus size={16} className="mr-2" /> New Webhook
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white rounded-sm max-w-md">
              <DialogHeader>
                <DialogTitle className="font-chivo text-2xl">Create Webhook</DialogTitle>
                <DialogDescription className="text-slate-600 font-ibm text-sm">
                  We&apos;ll POST a signed JSON payload to your endpoint for each subscribed event.
                </DialogDescription>
              </DialogHeader>

              {createdHook ? (
                <div className="space-y-3" data-testid="webhook-created-section">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <ShieldCheck size={18} />
                    <span className="font-ibm font-semibold text-sm">Webhook ready. Save the signing secret now.</span>
                  </div>
                  <div className="text-xs font-ibm uppercase tracking-widest text-slate-600">Signing Secret</div>
                  <div className="bg-slate-900 text-emerald-300 font-mono text-xs p-3 rounded-sm break-all flex items-center justify-between gap-2">
                    <span data-testid="webhook-secret">{createdHook.secret}</span>
                    <button
                      onClick={() => copy(createdHook.secret)}
                      data-testid="webhook-copy-secret"
                      className="p-1 hover:bg-slate-800 rounded-sm"
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                  <Button
                    onClick={() => setDialogOpen(false)}
                    data-testid="webhook-done"
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
                  >
                    Done
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleCreate} className="space-y-4" data-testid="webhook-form">
                  <div>
                    <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">URL</Label>
                    <Input
                      data-testid="webhook-url-input"
                      required
                      type="url"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="https://example.com/webhooks/finance"
                      className="mt-2 bg-slate-50 border-slate-200 rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Events</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {EVENT_OPTIONS.map((ev) => (
                        <label
                          key={ev.value}
                          className={`px-3 py-2 border rounded-sm cursor-pointer font-ibm text-xs flex items-center gap-2 ${
                            events.includes(ev.value)
                              ? 'border-slate-900 bg-slate-900 text-white'
                              : 'border-slate-300 text-slate-700 hover:border-slate-500'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={events.includes(ev.value)}
                            onChange={() => toggleEvent(ev.value)}
                            data-testid={`webhook-event-${ev.value.replace('.', '-')}`}
                            className="hidden"
                          />
                          {ev.label}
                        </label>
                      ))}
                    </div>
                  </div>
                  <Button
                    type="submit"
                    data-testid="webhook-submit-button"
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
                  >
                    Create Webhook
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-slate-600 font-ibm" data-testid="webhooks-loading">Loading…</div>
        ) : hooks.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-sm p-12 text-center" data-testid="webhooks-empty">
            <WebhookIcon className="mx-auto text-slate-400 mb-4" size={48} />
            <h3 className="text-xl font-chivo font-bold text-slate-900 mb-2">No webhooks yet</h3>
            <p className="text-slate-600 font-ibm text-sm">
              Subscribe to events to sync your data into Zapier, Make, Slack, or your own backend.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="webhooks-table">
            <table className="w-full">
              <thead className="bg-slate-900 text-white">
                <tr>
                  <th className="px-4 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">URL</th>
                  <th className="px-4 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Events</th>
                  <th className="px-4 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Status</th>
                  <th className="px-4 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Created</th>
                  <th className="px-4 py-4 text-right text-xs font-ibm font-semibold uppercase tracking-widest">Actions</th>
                </tr>
              </thead>
              <tbody>
                {hooks.map((h, idx) => (
                  <React.Fragment key={h.id}>
                  <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`webhook-row-${idx}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-900 break-all max-w-xs">
                      <button
                        onClick={() => toggleExpand(h.id)}
                        data-testid={`webhook-expand-${idx}`}
                        className="inline-flex items-center gap-1 hover:text-emerald-700"
                        title="View deliveries"
                      >
                        {expandedId === h.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        <span>{h.url}</span>
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(h.events || []).map((ev) => (
                          <span
                            key={ev}
                            className="text-[10px] font-ibm font-semibold uppercase tracking-widest px-2 py-0.5 rounded-sm bg-slate-100 text-slate-700"
                          >
                            {ev}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-[10px] font-ibm font-semibold uppercase tracking-widest px-2 py-0.5 rounded-sm ${
                          h.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        {h.active ? 'Active' : 'Paused'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-ibm text-xs text-slate-600">
                      {new Date(h.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDelete(h.id, h.url)}
                        data-testid={`webhook-delete-${idx}`}
                        className="p-2 hover:bg-red-50 rounded-sm transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={14} className="text-red-600" />
                      </button>
                    </td>
                  </tr>
                  {expandedId === h.id && (
                    <tr className="bg-slate-50/70" data-testid={`webhook-deliveries-${idx}`}>
                      <td colSpan={5} className="px-4 py-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2 text-slate-700">
                            <Activity size={14} />
                            <span className="font-ibm font-semibold text-sm">Recent Deliveries</span>
                          </div>
                          <button
                            onClick={() => fetchDeliveries(h.id)}
                            data-testid={`webhook-refresh-deliveries-${idx}`}
                            className="text-xs font-ibm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
                          >
                            <RefreshCw size={12} /> Refresh
                          </button>
                        </div>
                        {loadingDeliveries && !deliveries[h.id] ? (
                          <div className="text-xs font-ibm text-slate-500">Loading…</div>
                        ) : (deliveries[h.id] || []).length === 0 ? (
                          <div className="text-xs font-ibm text-slate-500">No deliveries yet for this webhook.</div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs font-ibm">
                              <thead className="text-slate-500">
                                <tr>
                                  <th className="px-2 py-1 text-left uppercase tracking-widest">Event</th>
                                  <th className="px-2 py-1 text-left uppercase tracking-widest">Status</th>
                                  <th className="px-2 py-1 text-left uppercase tracking-widest">Attempts</th>
                                  <th className="px-2 py-1 text-left uppercase tracking-widest">Last attempt</th>
                                  <th className="px-2 py-1 text-left uppercase tracking-widest">HTTP</th>
                                  <th className="px-2 py-1 text-right uppercase tracking-widest">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(deliveries[h.id] || []).map((d, di) => (
                                  <tr key={d.id} className="border-t border-slate-200" data-testid={`webhook-delivery-${idx}-${di}`}>
                                    <td className="px-2 py-1 font-mono text-[11px]">{d.event}</td>
                                    <td className="px-2 py-1">
                                      <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-sm ${STATUS_CLASSES[d.status] || 'bg-slate-100 text-slate-600'}`}>
                                        {d.status}
                                      </span>
                                    </td>
                                    <td className="px-2 py-1 tabular-nums">{d.attempts}/{d.max_attempts}</td>
                                    <td className="px-2 py-1 text-slate-600">
                                      {d.last_attempt_at ? new Date(d.last_attempt_at).toLocaleString() : '—'}
                                    </td>
                                    <td className="px-2 py-1 tabular-nums text-slate-600">{d.response_status ?? '—'}</td>
                                    <td className="px-2 py-1 text-right">
                                      {(d.status === 'failed' || d.status === 'dead') && (
                                        <button
                                          onClick={() => handleRetryDelivery(d.id, h.id)}
                                          data-testid={`webhook-delivery-retry-${idx}-${di}`}
                                          className="px-2 py-1 bg-slate-900 text-white rounded-sm hover:bg-slate-800 text-[10px] uppercase tracking-widest inline-flex items-center gap-1"
                                        >
                                          <RefreshCw size={10} /> Retry
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Webhooks;
